import PropTypes from 'prop-types'
import {
  MdPlayArrow,
  MdPause,
  MdStop,
  MdSchedule,
  MdAccessTime,
  MdPerson,
  MdTag,
  MdSettings,
  MdMonitor,
} from 'react-icons/md'

function SimulationCard({ simulation, onStart, onPause, onStop, onView, className = '' }) {
  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return <MdPlayArrow className="text-success-600 dark:text-success-400" />
      case 'paused':
      case 'on hold':
      case 'inactive':
        return <MdPause className="text-warning-600 dark:text-warning-400" />
      case 'completed':
      case 'finished':
      case 'stopped':
        return <MdStop className="text-neutral-600 dark:text-neutral-400" />
      default:
        return <MdSchedule className="text-neutral-500 dark:text-neutral-400" />
    }
  }

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'running':
        return 'bg-success-100 dark:bg-success-900 text-success-600 dark:text-success-400'
      case 'paused':
      case 'on hold':
      case 'inactive':
        return 'bg-warning-100 dark:bg-warning-900 text-warning-600 dark:text-warning-400'
      case 'completed':
      case 'finished':
      case 'stopped':
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400'
      default:
        return 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500 dark:text-neutral-400'
    }
  }

  const formatDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return 'N/A'
    const start = new Date(startDate)
    const end = new Date(endDate)
    const diffMs = end - start
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    return `${diffDays} days`
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const getParticipantCount = () => {
    const counts = {
      flights: simulation.flights?.length || 0,
      pilots: simulation.pilots?.length || 0,
      passengers: simulation.passengers?.length || 0,
      aircraft: simulation.aircraft?.length || 0,
      airports: simulation.airports?.length || 0,
      airlines: simulation.airlines?.length || 0,
      flightAttendants: simulation.flightAttendants?.length || 0,
    }
    return Object.values(counts).reduce((sum, count) => sum + count, 0)
  }

  return (
    <div
      className={`bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 hover:shadow-lg dark:hover:shadow-lg transition-shadow duration-200 ${className}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center">
            <MdMonitor className="text-white text-lg" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
              {simulation.name || `Simulation #${simulation.simulationNumber}`}
            </h3>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">
              {simulation.simulationNumber}
            </p>
          </div>
        </div>
        <span
          className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(simulation.status)}`}
        >
          {simulation.status || 'Unknown'}
        </span>
      </div>

      {simulation.description && (
        <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-2">
          {simulation.description}
        </p>
      )}

      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
            <MdAccessTime className="mr-1" />
            Duration
          </span>
          <span className="font-medium text-neutral-900 dark:text-white">
            {formatDuration(simulation.startDate, simulation.endDate)}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
            <MdSchedule className="mr-1" />
            Start Date
          </span>
          <span className="font-medium text-neutral-900 dark:text-white">
            {formatDate(simulation.startDate)}
          </span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
            <MdPerson className="mr-1" />
            Participants
          </span>
          <span className="font-medium text-neutral-900 dark:text-white">
            {getParticipantCount()}
          </span>
        </div>

        {simulation.configuration && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-neutral-600 dark:text-neutral-400 flex items-center">
              <MdSettings className="mr-1" />
              Mode
            </span>
            <span className="font-medium text-neutral-900 dark:text-white">
              {simulation.configuration.operationalMode || 'Standard'}
            </span>
          </div>
        )}
      </div>

      {simulation.tags && simulation.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-4">
          {simulation.tags.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-400"
            >
              <MdTag className="mr-1 text-xs" />
              {tag}
            </span>
          ))}
          {simulation.tags.length > 3 && (
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400">
              +{simulation.tags.length - 3} more
            </span>
          )}
        </div>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center space-x-2">
          {getStatusIcon(simulation.status)}
          <span className="text-sm text-neutral-600 dark:text-neutral-400">
            {simulation.status || 'Unknown'}
          </span>
          {simulation.version && (
            <span className="text-xs text-neutral-500 dark:text-neutral-500">
              v{simulation.version}
            </span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {onView && (
            <button
              onClick={() => onView(simulation)}
              className="px-3 py-1.5 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors duration-200"
            >
              View
            </button>
          )}

          {simulation.status?.toLowerCase() === 'inactive' && onStart && (
            <button
              onClick={() => onStart(simulation)}
              className="p-1.5 text-success-600 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 rounded-lg transition-colors duration-200"
              title="Start Simulation"
            >
              <MdPlayArrow className="text-lg" />
            </button>
          )}

          {simulation.status?.toLowerCase() === 'active' && onPause && (
            <button
              onClick={() => onPause(simulation)}
              className="p-1.5 text-warning-600 dark:text-warning-400 hover:bg-warning-50 dark:hover:bg-warning-900/20 rounded-lg transition-colors duration-200"
              title="Pause Simulation"
            >
              <MdPause className="text-lg" />
            </button>
          )}

          {(simulation.status?.toLowerCase() === 'active' ||
            simulation.status?.toLowerCase() === 'paused') &&
            onStop && (
              <button
                onClick={() => onStop(simulation)}
                className="p-1.5 text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 rounded-lg transition-colors duration-200"
                title="Stop Simulation"
              >
                <MdStop className="text-lg" />
              </button>
            )}
        </div>
      </div>
    </div>
  )
}

SimulationCard.propTypes = {
  simulation: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    simulationNumber: PropTypes.string,
    status: PropTypes.string,
    version: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    isArchived: PropTypes.bool,
    tags: PropTypes.arrayOf(PropTypes.string),
    configuration: PropTypes.object,
    history: PropTypes.array,
    flights: PropTypes.array,
    pilots: PropTypes.array,
    passengers: PropTypes.array,
    aircraft: PropTypes.array,
    airports: PropTypes.array,
    airlines: PropTypes.array,
    flightAttendants: PropTypes.array,
    bookings: PropTypes.array,
    jobs: PropTypes.array,
    bagages: PropTypes.array,
    createdBy: PropTypes.string,
  }).isRequired,
  onStart: PropTypes.func,
  onPause: PropTypes.func,
  onStop: PropTypes.func,
  onView: PropTypes.func,
  className: PropTypes.string,
}

export default SimulationCard
